import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { ACCENT_BLUE, ACCENT_BLUE_DARK, ERROR_RED } from '../../Assessments/Workspace/qcTheme';

// Admin QC-score override; the backend rejects a blank reason — every change is audit-logged.

const Header = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '22px 26px 16px',
  borderBottom: '1px solid rgba(0,0,0,0.12)',
});

const Title = styled('h2')({
  fontSize: '1.25rem',
  fontWeight: 400,
  color: 'rgba(0,0,0,0.87)',
  margin: 0,
});

const Subject = styled('p')({
  fontSize: '0.85rem',
  color: 'rgba(0,0,0,0.6)',
  margin: '6px 0 0',
});

const CloseButton = styled('button')({
  border: 'none',
  background: 'none',
  padding: 0,
  display: 'inline-flex',
  flex: 'none',
  cursor: 'pointer',
  color: 'rgba(0,0,0,0.55)',
  '&:hover': { color: 'rgba(0,0,0,0.8)' },
});

const Body = styled('div')({
  padding: '20px 26px 6px',
});

const ScoreRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '14px',
});

const ScoreCard = styled('div', {
  shouldForwardProp: prop => prop !== 'accent',
})<{ accent: boolean }>(({ accent }) => ({
  border: `1px solid ${accent ? 'rgba(25,118,210,0.5)' : 'rgba(0,0,0,0.12)'}`,
  borderRadius: '6px',
  padding: '14px 16px',
  background: accent ? 'rgba(25,118,210,0.03)' : '#fafbfe',
}));

const CardLabel = styled('div')({
  fontSize: '0.8rem',
  color: 'rgba(0,0,0,0.6)',
});

const CurrentValue = styled('div')({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.45)',
  marginTop: '6px',
});

const LockedNote = styled('div')({
  marginTop: '4px',
  fontSize: '0.68rem',
  color: 'rgba(0,0,0,0.45)',
});

const NewValueRow = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  gap: '6px',
  marginTop: '6px',
});

const ScoreInput = styled('input')({
  width: '74px',
  boxSizing: 'border-box',
  border: '1px solid rgba(0,0,0,0.42)',
  borderRadius: '4px',
  padding: '6px 10px',
  fontFamily: 'inherit',
  fontSize: '1.2rem',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.87)',
  outline: 'none',
  '&:focus': {
    borderColor: ACCENT_BLUE,
    boxShadow: `inset 0 0 0 1px ${ACCENT_BLUE}`,
  },
});

const PercentSign = styled('span')({
  fontSize: '1.2rem',
  fontWeight: 700,
  color: 'rgba(0,0,0,0.87)',
});

const SectionCaption = styled('div')({
  fontSize: '0.7rem',
  letterSpacing: '1px',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.6)',
  textTransform: 'uppercase',
  margin: '20px 0 8px',
});

const RequiredMark = styled('span')({
  color: ERROR_RED,
});

const ReasonArea = styled('textarea')({
  width: '100%',
  boxSizing: 'border-box',
  minHeight: '96px',
  border: '1px solid rgba(0,0,0,0.42)',
  borderRadius: '4px',
  padding: '10px 12px',
  fontFamily: 'inherit',
  fontSize: '0.9rem',
  color: 'rgba(0,0,0,0.87)',
  resize: 'vertical',
  outline: 'none',
  '&:focus': {
    borderColor: ACCENT_BLUE,
    boxShadow: '0 0 0 2px rgba(25,118,210,0.2)',
  },
});

const AttributionNote = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '9px',
  background: 'rgba(25,118,210,0.06)',
  border: '1px solid rgba(25,118,210,0.22)',
  borderRadius: '4px',
  padding: '10px 12px',
  marginTop: '14px',
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
    color: ACCENT_BLUE,
    marginTop: '1px',
    flex: 'none',
  },
});

const NoteText = styled('span')({
  fontSize: '0.8rem',
  lineHeight: 1.5,
  color: 'rgba(0,0,0,0.72)',
});

const Footer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '18px 26px 20px',
});

const CancelButton = styled('button')({
  background: 'transparent',
  border: 'none',
  color: ACCENT_BLUE,
  borderRadius: '4px',
  padding: '9px 14px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { background: 'rgba(25,118,210,0.06)' },
});

const SaveButton = styled('button')({
  background: ACCENT_BLUE,
  color: '#fff',
  border: 'none',
  borderRadius: '4px',
  padding: '9px 20px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  letterSpacing: '0.46px',
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'inherit',
  '&:hover': { background: ACCENT_BLUE_DARK },
  '&:disabled': {
    background: 'rgba(0,0,0,0.12)',
    color: 'rgba(0,0,0,0.32)',
    cursor: 'not-allowed',
  },
});

export interface EditQcScoreModalProps {
  open: boolean;
  subject: string;
  currentScore: number | null;
  // Write-once record of what the assessor was emailed at finalization; later corrections are in-house and never re-notify.
  emailedScore?: number | null;
  adminName: string;
  onSave: (score: number, reason: string) => void;
  onClose: () => void;
}

export default function EditQcScoreModal(props: EditQcScoreModalProps): JSX.Element {
  const { open, subject, currentScore, emailedScore = null, adminName, onSave, onClose } = props;
  const [score, setScore] = useState('');
  const [reason, setReason] = useState('');

  // Seed the draft from the row each time the dialog opens.
  useEffect(() => {
    if (open) {
      setScore(currentScore === null ? '' : String(currentScore));
      setReason('');
    }
  }, [open, currentScore]);

  const parsed = Number(score);
  const scoreValid = score.trim() !== '' && Number.isInteger(parsed) && parsed >= 0 && parsed <= 100;
  const canSave = scoreValid && reason.trim() !== '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label='Edit QC score'
      PaperProps={{ sx: { width: 620, maxWidth: '100%', borderRadius: '4px' } }}
      maxWidth={false}
    >
      <Header>
        <div>
          <Title>Edit QC Score</Title>
          <Subject>{subject}</Subject>
        </div>
        <CloseButton type='button' aria-label='Close edit QC score' onClick={onClose}>
          <CloseIcon sx={{ fontSize: 22 }} />
        </CloseButton>
      </Header>
      <Body>
        <ScoreRow>
          <ScoreCard accent={false}>
            <CardLabel>Emailed to Assessor</CardLabel>
            <CurrentValue>{emailedScore === null ? '—' : `${emailedScore}%`}</CurrentValue>
            <LockedNote>
              {emailedScore === null ? 'Not yet finalized' : 'Locked — sent once at finalization'}
            </LockedNote>
          </ScoreCard>
          <ScoreCard accent={false}>
            <CardLabel>Current Score</CardLabel>
            <CurrentValue>{currentScore === null ? '—' : `${currentScore}%`}</CurrentValue>
          </ScoreCard>
          <ScoreCard accent>
            <CardLabel>New Score</CardLabel>
            <NewValueRow>
              <ScoreInput
                aria-label='New score'
                value={score}
                onChange={event => setScore(event.target.value)}
              />
              <PercentSign>%</PercentSign>
            </NewValueRow>
          </ScoreCard>
        </ScoreRow>
        <SectionCaption>
          Reason for change <RequiredMark>*</RequiredMark>
        </SectionCaption>
        <ReasonArea
          aria-label='Reason for change'
          placeholder='Why is the score being changed? Recorded on the audit log.'
          value={reason}
          onChange={event => setReason(event.target.value)}
        />
        <AttributionNote>
          <HistoryIcon />
          <NoteText>
            Score changes are attributed to {adminName} and appear on the assessment audit log.
          </NoteText>
        </AttributionNote>
      </Body>
      <Footer>
        <CancelButton type='button' onClick={onClose}>Cancel</CancelButton>
        <SaveButton
          type='button'
          disabled={!canSave}
          onClick={() => onSave(parsed, reason.trim())}
        >
          Save Score
        </SaveButton>
      </Footer>
    </Dialog>
  );
}
